import {Type} from "class-transformer";
import {serializeType} from "./common";
import {ProjectWorkLocation} from "./project";
import {OrgAddress} from "./org-address";
import {EmployeeAddress} from "./employee";
import {PayCodeType, WorkLocationType} from "@app/shared";


export enum ChargeCodeProperty {
  ChargeNumber = 'chargeNumber',
  Name = 'name',
  Owner = 'ownerName',
  Description = 'description',
  AccountNumber = 'accountNumber',
  ProjectName = 'projectName',
  ProjectNumber = 'projectNumber',
  TaskName = 'taskName',
  ProjectLaborCategory = 'plcCode',
  PayTypeCode = 'payTypeCode'
}

export enum LaborCategoryProperty {
  AuthorizedFrom = 'authorizedFrom',
  AuthorizedThru = 'authorizedThru',
  AuthorizedHours = 'authorizedHours',
  LaborCategoryCode = 'laborCategoryCode',
  LaborCategoryId = 'laborCategoryId',
}

export class LaborCategoryInfo {
  authorizedFrom: string;
  authorizedThru: string;
  authorizedHours: number;
  actualHours: number;
  laborCategoryCode: string;
  laborCategoryId: number;
  workLocationFromHome: boolean;
  projectWorkLocations?: ProjectWorkLocation[];
}

class UserAddressInfo {
  @Type(serializeType(OrgAddress)) orgAddress: OrgAddress;
  @Type(serializeType(EmployeeAddress)) residentialAddress: EmployeeAddress;

  orgAddressId: number;
  residentialAddressId: number;
}

export class ChargeCode {
  id: number;
  version: number;
  @Type(serializeType(ChargeCode)) children?: ChargeCode[];      // backend populate this; when converted to a tree, this should be dropped

  owner: string;
  availableHours: number;
  authorizedHours: number;
  workedHours: number;
  hoursDidNotWork: number;
  isBillable: boolean;
  chargeNumber: string;
  name: string;
  description: string;
  allowCharging: boolean;
  directCharging: boolean;
  authorizedFrom: string;
  authorizedThru: string;
  effectiveFrom: string;
  effectiveThru: string;
  ownerId?: number;
  ownerName: string;
  parentId?: number;

  laborAccountGroupId?: number;
  laborAccountGroupName?: string;
  expenseAccountGroupId?: number;
  expenseAccountGroupName?: string;
  purchaseAccountGroupId?: number;
  purchaseAccountGroupName?: string;
  revenueAccountGroupId?: number;
  revenueAccountGroupName?: string;

  projectName?: string;
  taskName?: string;

  billable: boolean;
  codeType: string;

  @Type(serializeType(LaborCategoryInfo)) laborCategoryInfo?: LaborCategoryInfo[];
  @Type(serializeType(UserAddressInfo)) userAddressInfo?: UserAddressInfo;

  orgId?: number;  // Todo jC: remove this later
}

export class ChargeCodeSummary {
  id: number;
  chargeNumber: string;
  ownerName: string;
  accountNumber: string;

  chargeCodeId: number;
  allocationPercent: number;
  allocationAmount: number;
  taxAmount: number;
}

export class ChargeCodeTree {
  id: number;
  version: number;

  chargeNumber: string;
  name: string;
  activityDescription: string;
  allowCharging: boolean;
  // directCharging: boolean;
  authorizedFrom: string;
  authorizedThru: string;
  effectiveFrom: string;
  effectiveThru: string;

  ownerId?: number;
  ownerName: string;

  @Type(serializeType(ChargeCodeTree)) children?: ChargeCodeTree[];
  @Type(serializeType(ChargeCodeTree)) parent?: ChargeCodeTree;   // this value gets set by p-treetable
  expanded?: boolean;
  parentId?: number;

  laborAccountGroupId?: number;
  laborAccountGroupName?: string;
  expenseAccountGroupId?: number;
  expenseAccountGroupName?: string;
  purchaseAccountGroupId?: number;
  purchaseAccountGroupName?: string;
  revenueAccountGroupId?: number;
  revenueAccountGroupName?: string;
  isMappedToValidLaborGroup?: boolean;

  codeType: string;           // charge code type
  indirectType?: string;      // indirect charge code type
  payTypeId: number;
  payTypeCode: string;

  projectId?: number;
  projectName?: string;
  projectStartDate?: string;
  projectEndDate?: string;

  taskId?: number;
  taskName?: string;
  taskStartDate?: string;
  taskEndDate?: string;
}

export class PurchaseChargeCode extends ChargeCode {
  chargeCodeId: number;
  chargeCodeOwnerName: string;

  allocationPercent: number;
  allocationAmount: number;
  taxAmount: number;

  contractName: string;
  purchaseReqItemId: number;
}

export class ProjectChargeCodeLevel {
  projectId: number;
  chargeCodeLevel: number;
}

// call backend and remove
export enum ChargeCodeType {
  DirectProject = "DIRECT_PROJECT",
  IndirectProject = "INDIRECT_PROJECT",
  Indirect = "INDIRECT",
  Unallowable = "UNALLOWABLE"
}

export namespace ChargeCodeType {
  export function isDirect(chargeCodeType: ChargeCodeType): boolean {
    return chargeCodeType == ChargeCodeType.DirectProject;
  }

  export function isProjectType(chargeCodeType: ChargeCodeType): boolean {
    return chargeCodeType == ChargeCodeType.DirectProject || chargeCodeType == ChargeCodeType.IndirectProject;
  }

  export function isIndirect(chargeCodeType: ChargeCodeType): boolean {
    return chargeCodeType == ChargeCodeType.Indirect;
  }

  export function isUnallowable(chargeCodeType: ChargeCodeType): boolean {
    return chargeCodeType == ChargeCodeType.Unallowable;
  }

  export function isIndirectOrIndirectProject(chargeCodeType: ChargeCodeType): boolean {
    return chargeCodeType == ChargeCodeType.Indirect || chargeCodeType == ChargeCodeType.IndirectProject;
  }

  export function isNotDirect(chargeCodeType: ChargeCodeType): boolean {
    return chargeCodeType == ChargeCodeType.Indirect || chargeCodeType == ChargeCodeType.IndirectProject || chargeCodeType == ChargeCodeType.Unallowable;
  }

}

export enum IndirectChargeCodeType {
  Brevement = "BREV",
  Overhead = "OVERHEAD",
  PTO = "PTO",
  Sick = "SICK",
  Vacation = "VACATION",
}

export class ProjectChargeCode extends ChargeCode {
  projectId: number;
  projectName?: string;
  taskId?: number;
  taskName?: string;

  payTypeId: number;
  payTypeCode: PayCodeType;
  payTypeCodeName: string;
  referencePayTypeId?: number;

  laborCategoryCode: string;
  laborCategoryId: number;


  projectStartDate?: string;
  projectEndDate?: string;

  officeLocationId?: number;
  userResidenceId?: number;
  clientLocationId?: number;
  workLocationName?: string;
  taskStartDate?: string;
  taskEndDate?: string;

  defaultWorkLocation?: WorkLocationType;
}

export class UniqueChargeCode {
  chargeCodeId: number;
  allocationAmount: number;
  allocationPercent: number;
  taxAmount: number;
  chargeNumber: string;
  accountNumber: number;
}

export class PartnerInvoiceChargeCode extends UniqueChargeCode {
  partnerInvoiceItemId: number;
}

export class ProjectChargeCodeDropdown {
  label: string;
  value: ProjectChargeCode;
}

export class ProjectChargeCodeArray {
  [index: string]: ProjectChargeCodeDropdown[];
}

export class ProjectWorkLocationArray {
  [index: string]: ProjectWorkLocation[];
}
